import type { ReactNode } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTriggerButton,
  Tooltip,
  useModals,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useAnalytics } from '@/services/analytics';
import { ExportResultModal } from '@/features/exports';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useQueueDataIndexExport } from '@/services/db/data-index/exports';
import {
  dataIndexExportAnalyticsType,
  dataIndexExportMenu,
  type DataIndexExportChoice,
  type DataIndexExportObjectGroup,
} from '../utils/exports';

/**
 * The data index's CSV exports.
 *
 * Nothing downloads here. Each choice queues a job the backend builds out of
 * band and emails when it is ready, so the menu's whole answer is the modal
 * that says so; the file itself arrives on the workspace's exports page.
 */

const NO_PERMISSION_TOOLTIP = 'Contact an Admin to access this feature.';

export type ExportMenuProps = {
  workspaceId: number;
  /** Only a type's name and id reach the menu; the rest describes its schema. */
  objectTypes: readonly Pick<ObjectType, 'id' | 'name'>[];
};

export function ExportMenu({ workspaceId, objectTypes }: ExportMenuProps) {
  const { hasPermission } = usePermissions();
  const { account } = useAuth();
  const exports = useStartDataIndexExport(workspaceId);

  if (!hasPermission('journeys/exports/create')) {
    return <DisabledExportTrigger />;
  }

  // Object exports are a paid capability, so a workspace that cannot use
  // objects is not offered the object types it does not have.
  const menu = dataIndexExportMenu(objectTypes, { withObjects: !isBuilderPlan(account) });

  const item = (choice: DataIndexExportChoice) => (
    <DropdownMenuItem
      key={dataIndexExportAnalyticsType(choice.target)}
      isDisabled={exports.isPending}
      onClick={() => void exports.start(choice)}
    >
      {choice.label}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton variant="secondary" icon="chevron-down" iconPosition="trailing">
        Export to CSV
      </DropdownMenuTriggerButton>
      <DropdownMenuContent>
        <DropdownMenuGroup label="Profiles">{menu.profile.map(item)}</DropdownMenuGroup>
        {menu.objects.length > 0 ? (
          <DropdownMenuGroup label="Objects">
            {menu.objects.map((group) => (
              <ObjectTypeSubmenu key={group.objectTypeName} group={group} renderItem={item} />
            ))}
          </DropdownMenuGroup>
        ) : null}
        <DropdownMenuGroup label="Events">{menu.events.map(item)}</DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * What a viewer without the permission sees: the same button, saying why it
 * does nothing. A plain button rather than a menu trigger, because there is
 * no menu behind it to open.
 */
function DisabledExportTrigger() {
  // The tooltip attaches to the button rather than wrapping it: a default
  // Tooltip renders a button of its own, which cannot nest inside one.
  return (
    <Tooltip isCustom={true} content={NO_PERMISSION_TOOLTIP}>
      {(context) => (
        <Button
          variant="secondary"
          icon="chevron-down"
          iconPosition="trailing"
          isDisabled
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          Export to CSV
        </Button>
      )}
    </Tooltip>
  );
}

type ObjectTypeSubmenuProps = {
  group: DataIndexExportObjectGroup;
  renderItem: (choice: DataIndexExportChoice) => ReactNode;
};

function ObjectTypeSubmenu({ group, renderItem }: ObjectTypeSubmenuProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{group.objectTypeName}</DropdownMenuSubTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup label={`${group.objectTypeName} Attributes`}>
          {group.attributes.map(renderItem)}
        </DropdownMenuGroup>
        <DropdownMenuGroup label={`${group.objectTypeName} Relationships`}>
          {group.relationships.map(renderItem)}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenuSub>
  );
}

/**
 * Queue one export and report what became of it, the way the page this
 * replaces does: the pending modal when it was accepted, a snackbar when it
 * was refused.
 */
function useStartDataIndexExport(workspaceId: number) {
  const queueExport = useQueueDataIndexExport({ workspaceId });
  const modals = useModals();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();
  const router = useRouter();

  const exportsHref = router.buildLocation({ to: '/workspaces/$workspaceId/exports', params: { workspaceId } }).href;

  async function start(choice: DataIndexExportChoice): Promise<void> {
    const exportType = dataIndexExportAnalyticsType(choice.target);
    void analytics.track('dataIndex', 'exportStarted', { export_type: exportType });

    try {
      await queueExport.mutateAsync(choice.target);
    } catch {
      snackbars.create({
        id: `data-index-export-${exportType}`,
        variant: 'critical',
        message: "Your data couldn't be exported to CSV. Try again.",
      });
      return;
    }

    modals.open(ExportResultModal, { outcome: 'queued', exportsHref }, { shouldCloseOnUnmount: true });
  }

  return { start, isPending: queueExport.isPending };
}
